'use client'

/**
 * Catches the errors the React ErrorBoundary can't see — uncaught exceptions
 * in event handlers and unhandled promise rejections — and surfaces them as a
 * dismissible banner with a one-tap "Report issue" that snapshots the page,
 * grabs the stack trace, and opens the error report modal.
 *
 * The page is still alive for these errors (unlike a render crash), so the
 * screenshot captured at report time shows exactly what the user was looking
 * at. Noise (extension errors, network blips, aborted requests) is filtered by
 * shouldReportError, and a dismissed message stays muted for the session.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { useFeedbackStore } from '../stores/feedbackStore'
import {
  normalizeCaughtValue,
  shouldReportError,
} from '../lib/errorReport/errorReport'
import {
  initialBannerState,
  bannerErrorCaught,
  bannerDismissed,
  bannerReportOpened,
} from '../lib/errorReport/bannerState'
import { capturePageScreenshot } from '../lib/errorReport/captureScreenshot'

export function GlobalErrorBanner() {
  const [state, setState] = useState(initialBannerState)
  const [reporting, setReporting] = useState(false)
  const { openErrorReport } = useFeedbackStore()

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      const report = normalizeCaughtValue(event.error ?? event.message, 'window')
      if (!shouldReportError({ ...report, filename: event.filename })) return
      setState((s) => bannerErrorCaught(s, report))
    }
    const onRejection = (event: PromiseRejectionEvent) => {
      const report = normalizeCaughtValue(event.reason, 'promise')
      if (!shouldReportError(report)) return
      setState((s) => bannerErrorCaught(s, report))
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  if (!state.visible || !state.report) return null

  const report = state.report

  const handleReport = async () => {
    if (reporting) return
    setReporting(true)
    try {
      // Capture before the modal opens so the shot shows the page the error
      // happened on (the banner itself is excluded via the data attribute).
      const screenshot = await capturePageScreenshot()
      setState(bannerReportOpened)
      openErrorReport({
        report,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        screenshot,
      })
    } finally {
      setReporting(false)
    }
  }

  return (
    <div
      data-screenshot-exclude
      className="fixed top-3 inset-x-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-40 sm:w-full sm:max-w-xl"
      role="alert"
    >
      <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 shadow-lg px-4 py-3">
        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-red-800">
            Something went wrong
            {state.count > 1 && (
              <span className="ml-1.5 text-xs font-normal text-red-600">×{state.count}</span>
            )}
          </p>
          <p className="text-xs text-red-700 truncate">{report.message}</p>
        </div>
        <button
          onClick={handleReport}
          disabled={reporting}
          className="shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {reporting ? 'Capturing…' : 'Report issue'}
        </button>
        <button
          onClick={() => setState(bannerDismissed)}
          aria-label="Dismiss"
          className="shrink-0 p-1 text-red-400 hover:text-red-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
